import { useEffect, useState } from "react";
import axios from "axios";
import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { Loader2, Lock, CheckCircle2, AlertTriangle, CreditCard, X, User, Mail, ArrowRight } from "lucide-react";
import { getStripe } from "@/lib/stripe";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const currency = (cents) =>
  `$${(cents / 100).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const stripeAppearance = {
  theme: "night",
  variables: {
    colorPrimary: "#00f0ff",
    colorBackground: "#0a0510",
    colorText: "#ffffff",
    colorDanger: "#ff0055",
    fontFamily: "Inter, system-ui, sans-serif",
    borderRadius: "12px",
  },
};

const PayForm = ({ paymentIntentId, amountCents, isSubscription, onSuccess }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setBusy(true);
    setError("");
    try {
      const { error: submitError } = await elements.submit();
      if (submitError) {
        setError(submitError.message || "Payment couldn't be submitted");
        setBusy(false);
        return;
      }
      const { error: confirmError, paymentIntent } = await stripe.confirmPayment({
        elements,
        redirect: "if_required",
        confirmParams: {
          return_url: `${window.location.origin}/payment/success?pi=${paymentIntentId}`,
        },
      });
      if (confirmError) {
        setError(confirmError.message || "Payment failed. Try a different card.");
        setBusy(false);
        return;
      }
      if (paymentIntent && (paymentIntent.status === "succeeded" || paymentIntent.status === "processing")) {
        setDone(true);
        setTimeout(() => onSuccess({ paymentIntentId: paymentIntent.id }), 900);
      } else {
        setError("Payment is pending — we'll email you when it clears.");
        setBusy(false);
      }
    } catch (err) {
      setError(err?.message || "Unexpected error");
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="text-center py-6" data-testid="package-buy-success">
        <div className="w-14 h-14 rounded-full bg-[#22c55e]/15 border border-[#22c55e]/40 text-[#22c55e] flex items-center justify-center mx-auto mb-4">
          <CheckCircle2 size={24} />
        </div>
        <h3 className="font-display font-black text-2xl tracking-tight">
          {isSubscription ? "Retainer activated." : "Payment received."}
        </h3>
        <p className="text-[#a395c3] text-sm mt-2">
          {isSubscription
            ? "Your monthly retainer is live. We'll reach out to onboard you."
            : `${currency(amountCents)} confirmed. A receipt is on its way.`}
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="package-buy-form">
      <PaymentElement options={{ layout: "tabs" }} />
      {error && (
        <div role="alert" className="flex items-start gap-2 px-3 py-2 rounded-lg bg-[#ff0055]/10 border border-[#ff0055]/40 text-[#ff0055] text-xs">
          <AlertTriangle size={13} className="flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}
      <div className="flex items-center gap-2 text-[10px] text-white/40 uppercase tracking-[0.24em]">
        <Lock size={10} /> Secured by Stripe · card data never touches our servers
      </div>
      <button
        type="submit"
        disabled={!stripe || busy}
        data-testid="package-buy-submit"
        className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-full bg-[#00f0ff] text-black font-bold text-sm hover:bg-white transition-colors disabled:opacity-50"
      >
        {busy ? (
          <>
            <Loader2 size={14} className="animate-spin" /> Processing…
          </>
        ) : (
          <>
            <CreditCard size={14} />
            {isSubscription ? `Start · ${currency(amountCents)}/mo` : `Pay ${currency(amountCents)}`}
          </>
        )}
      </button>
    </form>
  );
};

/**
 * Two-step buy flow:
 *   Step 1 — collect name + email
 *   Step 2 — Stripe Elements to confirm payment
 *
 * Routes automatically:
 *   - recurring package → /payments/subscription/create
 *   - one-time package  → /payments/package/intent (mode=full)
 */
export default function PackageBuyModal({ open, onClose, packageInfo }) {
  const [step, setStep] = useState(0);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [intent, setIntent] = useState(null); // {client_secret, payment_intent_id, amount_cents, is_subscription}
  const [stripePromise] = useState(() => getStripe());

  useEffect(() => {
    if (open) {
      setStep(0); setName(""); setEmail(""); setError(""); setIntent(null);
    }
  }, [open]);

  if (!open || !packageInfo) return null;

  const isSubscription = !!packageInfo.recurring;
  const displayAmount = isSubscription ? packageInfo.amount_cents : packageInfo.amount_cents;

  const startPayment = async () => {
    setError("");
    if (!name.trim() || !email.trim()) {
      setError("Name and email are required.");
      return;
    }
    setLoading(true);
    try {
      const endpoint = isSubscription
        ? "/payments/subscription/create"
        : "/payments/package/intent";
      const body = isSubscription
        ? { package_key: packageInfo.key, email: email.trim(), name: name.trim() }
        : { package_key: packageInfo.key, email: email.trim(), name: name.trim(), mode: "full" };
      const { data } = await axios.post(`${API}${endpoint}`, body);
      setIntent({
        client_secret: data.client_secret,
        payment_intent_id: data.payment_intent_id,
        amount_cents: data.amount_cents,
      });
      setStep(1);
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't start the checkout. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const stripeOptions = intent?.client_secret ? { clientSecret: intent.client_secret, appearance: stripeAppearance } : null;

  return (
    <div
      className="fixed inset-0 z-[10000] bg-black/80 backdrop-blur-sm p-4 md:p-8 overflow-y-auto"
      onClick={onClose}
      data-testid="package-buy-modal"
    >
      <div
        className="max-w-md mx-auto rounded-3xl bg-[#0a0510] border border-white/10 p-6 md:p-7"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 mb-6">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-[#00f0ff] mb-1">
              {isSubscription ? "Monthly retainer" : "One-time purchase"}
            </div>
            <h2 className="font-display font-black text-2xl tracking-tight leading-tight">
              {packageInfo.name}
            </h2>
            <p className="text-[#a395c3] text-sm mt-1">
              {currency(displayAmount)}{isSubscription ? " · billed monthly · cancel anytime" : " · one-time"}
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-full bg-white/[0.05] border border-white/15 hover:bg-white/[0.12] flex items-center justify-center flex-shrink-0"
            aria-label="Close"
          >
            <X size={14} />
          </button>
        </div>

        {step === 0 && (
          <div className="space-y-4" data-testid="package-buy-contact-step">
            <div>
              <div className="text-[10px] uppercase tracking-[0.28em] text-[#a395c3] mb-1.5 flex items-center gap-2">
                <User size={11} /> Your name
              </div>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                data-testid="package-buy-name"
                className="w-full rounded-xl bg-white/[0.03] border border-white/10 px-4 py-3 text-sm focus:outline-none focus:border-[#00f0ff]/60"
                placeholder="Alex Rivera"
                autoFocus
              />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.28em] text-[#a395c3] mb-1.5 flex items-center gap-2">
                <Mail size={11} /> Email · we&apos;ll send the receipt here
              </div>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="package-buy-email"
                className="w-full rounded-xl bg-white/[0.03] border border-white/10 px-4 py-3 text-sm focus:outline-none focus:border-[#00f0ff]/60"
                placeholder="alex@studio.co"
              />
            </div>

            {error && (
              <div role="alert" className="flex items-start gap-2 px-3 py-2 rounded-lg bg-[#ff0055]/10 border border-[#ff0055]/40 text-[#ff0055] text-xs">
                <AlertTriangle size={13} className="flex-shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}

            <button
              type="button"
              onClick={startPayment}
              disabled={loading}
              data-testid="package-buy-continue"
              className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-full bg-[#00f0ff] text-black font-bold text-sm hover:bg-white transition-colors disabled:opacity-50"
            >
              {loading ? (
                <>
                  <Loader2 size={14} className="animate-spin" /> Preparing…
                </>
              ) : (
                <>
                  Continue to payment <ArrowRight size={14} />
                </>
              )}
            </button>
            <p className="text-[10px] text-white/40 text-center">
              Stripe processes the payment · we never see your card details.
            </p>
          </div>
        )}

        {step === 1 && intent && stripePromise && stripeOptions && (
          <Elements stripe={stripePromise} options={stripeOptions}>
            <PayForm
              paymentIntentId={intent.payment_intent_id}
              amountCents={intent.amount_cents}
              isSubscription={isSubscription}
              onSuccess={onClose}
            />
          </Elements>
        )}
      </div>
    </div>
  );
}
